import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { draftErrorMessage } from "@/shared/writing-assistant/classify-draft-error";
import { prepareDraftNotes } from "@/shared/writing-assistant/prepare-draft-notes";
import { useGenerateDraft } from "@/shared/writing-assistant/use-generate-draft";
import type {
  DraftContext,
  WritingAssistantSurface,
} from "@/shared/writing-assistant/writing-assistant.schema";

interface UseWritingAssistArgs {
  workspaceId: string;
  surface: WritingAssistantSurface;
  /// Read the current textarea value from the form.
  getValue: () => string;
  /// Write the draft back into the form (dirty + validated).
  setValue: (text: string) => void;
  /// Compose-form context snapshot at generation time.
  buildContext: () => DraftContext | undefined;
}

/// Per-dialog assist state: one generation in flight max, one snapshot of
/// the pre-generation text powering "Revert to my notes" (spec decision 2).
export function useWritingAssist({
  workspaceId,
  surface,
  getValue,
  setValue,
  buildContext,
}: UseWritingAssistArgs) {
  const mut = useGenerateDraft(workspaceId);
  const snapshotRef = useRef<string | null>(null);
  const [canRevert, setCanRevert] = useState(false);

  const generate = useCallback(async () => {
    const input = prepareDraftNotes(getValue());
    if (input === null || mut.isPending) return;
    // Snapshot the trimmed value now, before generation, so "Revert to my
    // notes" restores what the user had when they clicked (spec decision 2).
    const snapshot = getValue().trim();
    try {
      // Call buildContext() once so the snapshot is consistent across the spread.
      const context = buildContext();
      const result = await mut.mutateAsync({
        surface,
        input,
        ...(context ? { context } : {}),
      });
      snapshotRef.current = snapshot;
      setCanRevert(true);
      setValue(result.draft.body);
    } catch (err) {
      toast.error(draftErrorMessage(err));
    }
  }, [getValue, setValue, buildContext, mut.mutateAsync, mut.isPending, surface]);

  const revert = useCallback(() => {
    if (snapshotRef.current === null) return;
    setValue(snapshotRef.current);
    snapshotRef.current = null;
    setCanRevert(false);
  }, [setValue]);

  const reset = useCallback(() => {
    snapshotRef.current = null;
    setCanRevert(false);
    mut.reset();
  }, [mut.reset]);

  return {
    isGenerating: mut.isPending,
    canRevert,
    generate,
    revert,
    /// Call from the dialog's open-reset effect so stale snapshots don't
    /// survive a close/reopen.
    reset,
  };
}

export type WritingAssist = ReturnType<typeof useWritingAssist>;
